import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { getCustomersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Customer } from '../api/generated/model';
import CustomerDetailContent from './CustomerDetailContent';
import { CopyButton } from './common/Copyable';

interface CustomerDetailModalProps {
  show: boolean;
  onHide: () => void;
  customer?: Customer | null;
  customerId?: string | null;
}

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({ show, onHide, customer: customerProp, customerId }) => {
  const { data: fetchedCustomer, isLoading, refetch } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomersId(customerId!),
    enabled: show && !!customerId && !customerProp,
  });

  const customer = customerProp || fetchedCustomer || null;

  if (!show) return null;

  if (isLoading && !customer) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" className="me-2" />
          Loading customer...
        </Modal.Body>
      </Modal>
    );
  }

  if (!customer) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3 flex-wrap">
          <span>{customer.name}</span>
          {customer.id && (
            <div className="d-flex gap-2 bg-header-control rounded px-2 py-1">
              <a
                href={`/customers/${customer.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Open
              </a>
              <CopyButton
                value={`${window.location.origin}/customers/${customer.id}`}
                variant="outline-primary"
                size="sm"
                title="Copy link to clipboard"
              >
                <i className="bi bi-link-45deg me-1"></i>
                Copy Link
              </CopyButton>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3 d-flex flex-column" style={{ minHeight: 0 }}>
        <CustomerDetailContent customer={customer} onRefresh={refetch} />
      </Modal.Body>
    </Modal>
  );
};

export default CustomerDetailModal;
